import { Injectable } from '@angular/core';
import { GA4_CURRENCY, Ga4Item } from '@biletix/core/common/utils/ga4-ecommerce.util';
import { GtmService } from '@biletix/core/services/gtm.service';

/**
 * GA4 liste event'lerini (`view_item_list`, `select_item`) gönderen ince katman.
 *
 * Amaç, aynı `ecommerce` gövdesini her liste bileşeninde tekrar kurmamak:
 * repodaki mevcut UA push'ları bu şekilde 6 dosyaya birebir kopyalanmış
 * (`createProductsDataLayer`). `items[]` içeriği çağıran bileşende
 * `buildGa4EventItem` ile üretilir; liste kimliği ve tekrar gönderim koruması
 * burada yönetilir.
 *
 * Platform guard'ı `GtmService` içinde olduğu için burada ayrıca kontrol yok.
 */
@Injectable({ providedIn: 'root' })
export class Ga4EcommerceService {
  /**
   * Aynı listenin aynı içerikle tekrar gönderilmesini engelleyen imza kümesi.
   *
   * Liste bileşenleri `@Input` setter'ları ve `setTimeout`'lar üzerinden aynı
   * diziyi birden çok kez yayabiliyor (ör. `homepage-tabbed-slider`'ın sekme
   * değişiminde iki kez set edilen `categories`); imza olmadan GA4'te tek
   * görüntüleme çoklu `view_item_list` olarak raporlanır.
   */
  private sentListSignatures = new Set<string>();

  constructor(private gtmService: GtmService) {}

  /**
   * `view_item_list` gönderir. Aynı liste + aynı ürün kümesi ikinci kez
   * gönderilmez; liste gerçekten değiştiğinde (sekme/sayfa değişimi) imza da
   * değiştiği için yeni event geçer.
   */
  pushViewItemList(listId: string, listName: string, items: Ga4Item[]): void {
    if (!items.length) {
      return;
    }

    const signature = `${listId}|${items.map((item) => item.item_id).join(',')}`;
    if (this.sentListSignatures.has(signature)) {
      return;
    }
    this.sentListSignatures.add(signature);

    this.gtmService.pushEcommerce('view_item_list', {
      item_list_id: listId,
      item_list_name: listName,
      currency: GA4_CURRENCY,
      items: items.map((item, index) => ({
        ...item,
        item_list_id: listId,
        item_list_name: listName,
        index: item.index ?? index,
      })),
    });
  }

  /**
   * `select_item` gönderir.
   *
   * ⚠️ Kart bağlantılarının bir kısmı `[href]` (tam sayfa yüklemesi): o durumda
   * push, navigasyonla yarışır ve event kaybedilebilir. Navigasyonu geciktirmek
   * (preventDefault + GTM `eventCallback`) kullanıcı deneyimini bozduğu için
   * bilinçli olarak yapılmıyor; `routerLink` kullanan kartlarda (çoğunluk)
   * event güvenilir şekilde gider.
   */
  pushSelectItem(listId: string, listName: string, item: Ga4Item): void {
    this.gtmService.pushEcommerce('select_item', {
      item_list_id: listId,
      item_list_name: listName,
      items: [{ ...item, item_list_id: listId, item_list_name: listName }],
    });
  }
}
